import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import socket from '../services/socket';
import { friends } from '../services/api';
import { useAuth } from '../context/AuthContext';
import styles from './InviteListener.module.css';

// Глобальный слушатель приглашений «за стол» — паритет с APK GlobalOverlay.
// Ловит friend:invite на ЛЮБОЙ странице (компонент висит вне Routes) и показывает
// модал «X зовёт за стол» с обратным отсчётом. Принять → входим в комнату.
// Плюс friend:inviteResult — тост пригласившему («друг принял/отклонил»).
const InviteListener = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);   // { inviteId, from, roomId, joinCode, expiresIn }
  const [left, setLeft] = useState(0);           // секунд до истечения
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const tickRef = useRef(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2500); };

  // Подписка на сокет — пока пользователь залогинен.
  useEffect(() => {
    if (!user) return undefined;
    if (!socket.connected) socket.connect();

    const onInvite = (data) => {
      if (!data?.inviteId) return;
      setInvite(data);
      setLeft(Math.max(0, Number(data.expiresIn) || 60));
    };
    const onResult = (data) => {
      flash(data?.accepted ? 'Друг принял приглашение' : 'Друг отклонил приглашение');
    };

    socket.on('friend:invite', onInvite);
    socket.on('friend:inviteResult', onResult);
    return () => {
      socket.off('friend:invite', onInvite);
      socket.off('friend:inviteResult', onResult);
    };
  }, [user]);

  // Обратный отсчёт — когда дошёл до нуля, прячем модал (приглашение истекло).
  useEffect(() => {
    if (!invite) { if (tickRef.current) clearInterval(tickRef.current); return undefined; }
    tickRef.current = setInterval(() => {
      setLeft((s) => {
        if (s <= 1) { setInvite(null); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(tickRef.current);
  }, [invite]);

  if (!invite) return toast ? <div className={styles.toast}>{toast}</div> : null;

  const who = invite.from?.displayName || invite.from?.username || 'Друг';
  const avatar = invite.from?.avatar;

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await friends.acceptInvite(invite.inviteId);
      const roomId = res?.roomId || invite.roomId;
      setInvite(null);
      navigate(`/room/${roomId}`);
    } catch (e) {
      flash(e.message || 'Не удалось войти');
      setInvite(null);
    } finally { setBusy(false); }
  };

  const decline = async () => {
    if (busy) return;
    setBusy(true);
    const id = invite.inviteId;
    setInvite(null);
    try { await friends.declineInvite(id); } catch { /* приглашение могло истечь — молча */ }
    setBusy(false);
  };

  return (
    <>
      <div className={styles.backdrop} onClick={decline}>
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.avatar}>
            {avatar ? <img src={avatar} alt="" /> : who.charAt(0).toUpperCase()}
          </div>
          <div className={styles.who}>{who}</div>
          <div className={styles.sub}>зовёт за стол</div>
          <div className={styles.timer}>{left} с</div>
          <div className={styles.actions}>
            <button className={styles.decline} onClick={decline} disabled={busy}>Отклонить</button>
            <button className={styles.accept} onClick={accept} disabled={busy}>За стол</button>
          </div>
        </div>
      </div>
      {toast && <div className={styles.toast}>{toast}</div>}
    </>
  );
};

export default InviteListener;
